﻿import { config } from '@/config/env';
import { showGlobalToast } from '@/components/common/Toast';

let refreshPromise: Promise<boolean> | null = null;

class ApiClient {
  private baseURL: string;
  private cache = new Map<string, { data: unknown; timestamp: number }>();
  private cacheTime = 5 * 60 * 1000;
  private refreshThreshold = 5 * 60 * 1000; // 5 minutes before expiry

  constructor(baseURL?: string) {
    this.baseURL = baseURL || config.WSREST_TABATA;
  }

  /**
   * Get token, refreshing if needed (within 5 min of expiry)
   */
  private async getValidToken(): Promise<string | null> {
    const token = sessionStorage.getItem('mT');
    const exp = sessionStorage.getItem('mEx');
    const refreshToken = sessionStorage.getItem('mR');
    const userid = sessionStorage.getItem('mID');

    if (!token || !exp) return null;

    const expTime = Date.parse(exp);
    const needsRefresh = expTime - this.refreshThreshold < Date.now();

    if (!needsRefresh) return token;

    // Token is about to expire or expired - try refresh
    if (!refreshToken || !userid) {
      // No refresh token, session is dead
      return null;
    }

    // Prevent multiple simultaneous refresh calls
    if (!refreshPromise) {
      refreshPromise = this.refreshToken(userid, refreshToken)
        .finally(() => { refreshPromise = null; });
    }

    try {
      const ok = await refreshPromise;
      if (ok) return sessionStorage.getItem('mT');
      return null;
    } catch {
      return null;
    }
  }

  /**
   * Refresh the access token
   */
  private async refreshToken(userid: string, refreshToken: string): Promise<boolean> {
    try {
      console.log('Refreshing token...');
      const base = this.baseURL.replace(/\/+$/, '');
      const url = base + '/refreshtoken/' + config.SITE + '/' + config.NOME_APP;

      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userid, token: refreshToken }),
      });

      if (!res.ok) {
        console.error('Token refresh failed:', res.status);
        return false;
      }

      const data = await res.json();
      sessionStorage.setItem('mT', data.t);
      sessionStorage.setItem('mEx', data.tex);
      // Keep same refresh token
      sessionStorage.setItem('mR', refreshToken);
      console.log('Token refreshed successfully');
      return true;
    } catch (err) {
      console.error('Token refresh error:', err);
      return false;
    }
  }

  private buildURL(endpoint: string, params: Record<string, string | number> = {}): string {
    const base = this.baseURL.replace(/\/+$/, '');
    const path = endpoint.replace(/^\/+/, '');
    const fullPath = base + '/' + path;

    const qs = Object.entries(params)
      .filter(([, v]) => v !== null && v !== undefined)
      .map(([k, v]) => encodeURIComponent(k) + '=' + encodeURIComponent(String(v)))
      .join('&');

    return qs ? fullPath + '?' + qs : fullPath;
  }

  async request<T>(endpoint: string, options: {
    method?: string;
    body?: unknown;
    params?: Record<string, string | number>;
    requiresAuth?: boolean;
    cache?: boolean;
    signal?: AbortSignal;
  } = {}): Promise<T> {
    const { method = 'GET', body, params = {}, requiresAuth = true, cache = false, signal } = options;
    const url = this.buildURL(endpoint, params);

    if (method === 'GET' && cache) {
      const cached = this.cache.get(url);
      if (cached && Date.now() - cached.timestamp < this.cacheTime) return cached.data as T;
    }

    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (requiresAuth) {
      const token = await this.getValidToken();
      if (!token) {
        this.handleSessionExpired();
        throw new Error('Authentication required');
      }
      headers['Authorization'] = 'Bearer ' + token;
    }

    const res = await fetch(url, {
      method,
      headers,
      body: body && method !== 'GET' ? JSON.stringify(body) : undefined,
      signal,
    });

    // Handle 401 - try refresh once, then fail
    if (res.status === 401) {
      const refreshToken = sessionStorage.getItem('mR');
      const userid = sessionStorage.getItem('mID');

      if (refreshToken && userid) {
        const refreshed = await this.refreshToken(userid, refreshToken);
        if (refreshed) {
          // Retry the request with new token
          const newToken = sessionStorage.getItem('mT');
          headers['Authorization'] = 'Bearer ' + newToken;
          const retryRes = await fetch(url, {
            method,
            headers,
            body: body && method !== 'GET' ? JSON.stringify(body) : undefined,
          });
          if (retryRes.status === 401) {
            this.handleSessionExpired();
            throw new Error('Session expired');
          }
          if (retryRes.status === 204) return null as T;
          if (!retryRes.ok) {
            const error: any = new Error('HTTP ' + retryRes.status);
            error.status = retryRes.status;
            throw error;
          }
          const data = await retryRes.json();
          if (method === 'GET' && cache) this.cache.set(url, { data, timestamp: Date.now() });
          return data as T;
        }
      }

      this.handleSessionExpired();
      throw new Error('Session expired');
    }

    if (res.status === 204) return null as T;
    if (!res.ok) {
      const error: any = new Error('HTTP ' + res.status + ': ' + res.statusText);
      error.status = res.status;
      try { error.data = await res.json(); } catch {}
      throw error;
    }

    const data = await res.json();
    if (method === 'GET' && cache) this.cache.set(url, { data, timestamp: Date.now() });
    return data as T;
  }

  private handleSessionExpired() {
    sessionStorage.clear();
    localStorage.removeItem('ruolo');
    this.cache.clear();

    if (!window.location.pathname.includes('/login')) {
      showGlobalToast('Sessione scaduta', 'Effettua nuovamente il login.', 'warning');
      setTimeout(() => { window.location.href = '/login'; }, 2000);
    }
  }

  get<T>(endpoint: string, opts: { params?: Record<string, string | number>; requiresAuth?: boolean; cache?: boolean; signal?: AbortSignal } = {}) { return this.request<T>(endpoint, { ...opts, method: 'GET' }); }
  post<T>(endpoint: string, body: unknown, opts: { params?: Record<string, string | number>; requiresAuth?: boolean; cache?: boolean; signal?: AbortSignal } = {}) { return this.request<T>(endpoint, { ...opts, method: 'POST', body }); }
  put<T>(endpoint: string, body: unknown, opts: { params?: Record<string, string | number>; requiresAuth?: boolean; cache?: boolean; signal?: AbortSignal } = {}) { return this.request<T>(endpoint, { ...opts, method: 'PUT', body }); }
  del<T>(endpoint: string, opts: { params?: Record<string, string | number>; requiresAuth?: boolean; cache?: boolean; signal?: AbortSignal } = {}) { return this.request<T>(endpoint, { ...opts, method: 'DELETE' }); }

  clearCache() { this.cache.clear(); }
}

export const api = new ApiClient();

